import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { from, merge, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  exhaustMap,
  filter,
  map,
  mergeMap,
  pairwise,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';

import { CustomerRegistrationType } from 'ish-core/models/customer/customer.model';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { PaymentService } from 'ish-core/services/payment/payment.service';
import { TokenService } from 'ish-core/services/token/token.service';
import { UserService } from 'ish-core/services/user/user.service';
import { displaySuccessMessage } from 'ish-core/store/core/messages';
import { selectQueryParam, selectUrl } from 'ish-core/store/core/router';
import { getServerConfigParameter } from 'ish-core/store/core/server-config';
import { ApiTokenService } from 'ish-core/utils/api-token/api-token.service';
import { mapErrorToAction, mapToPayload, mapToPayloadProperty, whenTruthy } from 'ish-core/utils/operators';

import { getPGID, personalizationStatusChanged, personalizationStatusDetermined } from '.';
import {
  clearMfaChallenge,
  clearRegistrationBudget,
  createUser,
  createUserApprovalRequired,
  createUserFail,
  createUserSuccess,
  deleteUserPaymentInstrument,
  deleteUserPaymentInstrumentFail,
  deleteUserPaymentInstrumentSuccess,
  fetchAnonymousUserToken,
  loadCompanyUser,
  loadCompanyUserFail,
  loadCompanyUserSuccess,
  loadUserByAPIToken,
  loadUserCostCenters,
  loadUserCostCentersFail,
  loadUserCostCentersSuccess,
  loadUserPaymentMethods,
  loadUserPaymentMethodsFail,
  loadUserPaymentMethodsSuccess,
  loginUser,
  loginUserFail,
  loginUserMfaRequired,
  loginUserSuccess,
  loginUserWithToken,
  logoutUser,
  logoutUserFail,
  logoutUserSuccess,
  requestPasswordReminder,
  requestPasswordReminderFail,
  requestPasswordReminderSuccess,
  resendMfaOtp,
  resendMfaOtpFail,
  resendMfaOtpSuccess,
  resetUserData,
  updateCustomer,
  updateCustomerFail,
  updateCustomerSuccess,
  updateUser,
  updateUserFail,
  updateUserPassword,
  updateUserPasswordByPasswordReminder,
  updateUserPasswordByPasswordReminderFail,
  updateUserPasswordByPasswordReminderSuccess,
  updateUserPasswordFail,
  updateUserPasswordSuccess,
  updateUserPreferredPayment,
  updateUserSuccess,
  userErrorReset,
  userNewsletterActions,
  verifyMfaOtp,
  verifyMfaOtpFail,
} from './user.actions';
import { getLoggedInCustomer, getLoggedInUser, getRegistrationBudget, getUserError } from './user.selectors';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private userService: UserService,
    private paymentService: PaymentService,
    private router: Router,
    private apiTokenService: ApiTokenService,
    private tokenService: TokenService
  ) {}

  loginUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginUser),
      mapToPayloadProperty('credentials'),
      exhaustMap(credentials =>
        this.userService.signInUser(credentials).pipe(
          map(loginUserSuccess),
          // DIAS Customization Start - Email OTP MFA for ICM local login
          // an MFA_REQUIRED response is not a login failure - divert it to its own action before the
          // generic mapErrorToAction(loginUserFail) below would otherwise treat it as invalid credentials
          catchError((error: HttpError) =>
            error?.code === 'MFA_REQUIRED' && error.mfaChallenge
              ? of(loginUserMfaRequired({ mfaChallenge: error.mfaChallenge }))
              : throwError(() => error)
          ),
          // DIAS Customization End
          mapErrorToAction(loginUserFail)
        )
      )
    )
  );

  // DIAS Customization Start - Email OTP MFA for ICM local login
  verifyMfaOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyMfaOtp),
      mapToPayloadProperty('otp'),
      exhaustMap(otp =>
        // on success the same 'loginUserSuccess' action is dispatched as a normal login - this reuses all
        // existing success handling (token storage, navigation, business-user loading, ...) unchanged
        this.userService.verifyMfaOtp(otp).pipe(map(loginUserSuccess), mapErrorToAction(verifyMfaOtpFail))
      )
    )
  );

  resendMfaOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resendMfaOtp),
      exhaustMap(() =>
        this.userService.resendMfaOtp().pipe(
          map(() => resendMfaOtpSuccess()),
          mapErrorToAction(resendMfaOtpFail)
        )
      )
    )
  );

  restartLoginAfterMfaFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyMfaOtpFail, resendMfaOtpFail),
      mapToPayloadProperty('error'),
      filter(error => !!error.mfaRestartRequired),
      concatMap(error => [clearMfaChallenge(), loginUserFail({ error })])
    )
  );

  registrationMfaLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginUserMfaRequired),
        concatLatestFrom(() => [
          this.store.pipe(select(selectUrl)),
          this.store.pipe(select(selectQueryParam('returnUrl'))),
        ]),
        filter(([, url]) => /^\/register(?:[/?]|$)/.test(url ?? '')),
        concatMap(([, , returnUrl]) =>
          from(
            this.router.navigate(['/login'], {
              queryParams: { returnUrl: returnUrl || '/account' },
              replaceUrl: true,
            })
          )
        )
      ),
    { dispatch: false }
  );

  updateRegistrationBudget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanyUserSuccess),
      concatLatestFrom(() => [
        this.store.pipe(select(getLoggedInCustomer)),
        this.store.pipe(select(getRegistrationBudget)),
      ]),
      filter(([, , pending]) => !!pending),
      concatMap(([{ payload }, customer, pending]) => [
        clearRegistrationBudget(),
        ...(customer?.isBusinessCustomer && payload.user.email === pending.email
          ? [updateCustomer({ customer: { ...customer, budgetPriceType: pending.budgetPriceType } })]
          : []),
      ])
    )
  );

  clearMfaChallenge$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(clearMfaChallenge, loginUserSuccess, loginUserFail, logoutUser, resetUserData),
        map(() => this.userService.clearMfaChallenge())
      ),
    { dispatch: false }
  );
  // DIAS Customization End

  /**
   * Revoke token on server side
   */
  logoutUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutUser),
      switchMap(() =>
        this.userService.logoutUser().pipe(
          mergeMap(() => [logoutUserSuccess(), resetUserData()]),
          mapErrorToAction(logoutUserFail)
        )
      )
    )
  );

  fetchAnonymousUserToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchAnonymousUserToken),
        switchMap(() => this.tokenService.fetchToken('anonymous'))
      ),
    { dispatch: false }
  );

  loginUserWithToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginUserWithToken),
      mapToPayloadProperty('token'),
      exhaustMap(token =>
        this.userService.signInUserByToken(token).pipe(map(loginUserSuccess), mapErrorToAction(loginUserFail))
      )
    )
  );

  loadCompanyUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCompanyUser),
      mergeMap(() =>
        this.userService.getCompanyUserData().pipe(
          map(user => loadCompanyUserSuccess({ user })),
          mapErrorToAction(loadCompanyUserFail)
        )
      )
    )
  );

  /**
   * redirects to the returnUrl after successful login
   * does not redirect at all, if no returnUrl is defined
   *
   * CUSTOMIZATION: multi-affiliate users (availableSoldTos > 1) stay on Sign-in so
   * LoginPageComponent can show the account picker before navigating away (MVP 8.2.1).
   */
  redirectAfterLogin$ =
    !SSR &&
    createEffect(
      () =>
        this.actions$.pipe(
          ofType(loginUserSuccess),
          withLatestFrom(this.store.pipe(select(selectQueryParam('returnUrl')))),
          filter(([action, returnUrl]) => {
            if (!returnUrl) {
              return false;
            }
            const affiliateCount = action.payload?.user?.availableSoldTos?.length ?? 0;
            return affiliateCount <= 1;
          }),
          concatMap(([, navigateTo]) => from(this.router.navigateByUrl(navigateTo)))
        ),
      { dispatch: false }
    );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createUser),
      mapToPayload(),
      // DIAS Customization: exhaustMap instead of mergeMap - a second submit while the first registration is still
      // running (ICM answers slowly as it waits for the registration mail) must not create the user twice
      exhaustMap((data: CustomerRegistrationType) =>
        this.userService.createUser(data).pipe(
          withLatestFrom(
            this.store.pipe(select(getServerConfigParameter<string[]>('general.customerTypeForLoginApproval')))
          ),
          concatMap(([createUserResponse, customerTypeForLoginApproval]) => [
            createUserSuccess({
              email: createUserResponse.user.email,
              ...(createUserResponse.customer.isBusinessCustomer && createUserResponse.customer.budgetPriceType
                ? { budgetPriceType: createUserResponse.customer.budgetPriceType }
                : {}),
            }),
            ...(data.subscribedToNewsletter
              ? [
                  userNewsletterActions.updateUserNewsletterSubscription({
                    subscriptionStatus: true,
                    userEmail: createUserResponse.user.email,
                  }),
                ]
              : []),
            customerTypeForLoginApproval?.includes(createUserResponse.customer.isBusinessCustomer ? 'SMB' : 'PRIVATE')
              ? createUserApprovalRequired({ email: createUserResponse.user.email })
              : loginUser({ credentials: data.credentials }),
          ]),
          mapErrorToAction(createUserFail)
        )
      )
    )
  );

  redirectAfterUserCreationWithCustomerApproval$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createUserApprovalRequired),
        concatMap(() => from(this.router.navigate(['/register/approval'])))
      ),
    { dispatch: false }
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      concatMap(([{ user, credentials, successMessage }, customer]) =>
        this.userService.updateUser({ user, customer }, credentials).pipe(
          map(changedUser => updateUserSuccess({ user: changedUser, successMessage })),
          mapErrorToAction(updateUserFail)
        )
      )
    )
  );

  updateUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserPassword),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInUser))),
      concatMap(([[payload, customer], user]) =>
        this.userService.updateUserPassword(customer, user, payload.password, payload.currentPassword).pipe(
          map(() =>
            updateUserPasswordSuccess({
              successMessage: payload.successMessage || { message: 'account.profile.update_password.message' },
            })
          ),
          mapErrorToAction(updateUserPasswordFail)
        )
      )
    )
  );

  updateCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCustomer),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      filter(([, loggedInCustomer]) => !!loggedInCustomer && loggedInCustomer.isBusinessCustomer),
      concatMap(([{ customer, successMessage }]) =>
        this.userService.updateCustomer(customer).pipe(
          map(changedCustomer => updateCustomerSuccess({ customer: changedCustomer, successMessage })),
          mapErrorToAction(updateCustomerFail)
        )
      )
    )
  );

  redirectAfterUpdateOnProfileSettings$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserSuccess, updateCustomerSuccess, updateUserPasswordSuccess),
        concatLatestFrom(() => this.store.pipe(select(selectUrl))),
        filter(([, url]) => url.includes('/account/profile') || url.includes('/account/organization/settings')),
        concatMap(([, url]) =>
          from(
            this.router.navigateByUrl(
              url.includes('/account/profile') ? '/account/profile' : '/account/organization/settings'
            )
          )
        )
      ),
    { dispatch: false }
  );

  displayUpdateUserSuccessMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserPasswordSuccess, updateUserSuccess, updateCustomerSuccess),
      mapToPayloadProperty('successMessage'),
      filter(successMessage => !!successMessage),
      map(displaySuccessMessage)
    )
  );

  resetUserError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      concatLatestFrom(() => this.store.pipe(select(getUserError))),
      filter(([, error]) => !!error),
      map(() => userErrorReset())
    )
  );

  loadCompanyUserAfterLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginUserSuccess),
      mapToPayload(),
      filter(payload => payload.customer.isBusinessCustomer),
      map(() => loadCompanyUser())
    )
  );

  loadUserByAPIToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserByAPIToken),
      mapToPayloadProperty('apiToken'),
      concatMap(token =>
        this.userService.signInUserByToken(token).pipe(map(loginUserSuccess), mapErrorToAction(loginUserFail))
      )
    )
  );

  /**
   * This effect emits the 'personalizationStatusDetermined' action once a PGID is fetched or changed or if there is no user apiToken cookie.
   * It also emits the 'personalizationStatusChanged' action when the PGID actually changes (this includes changes from an to 'undefined').
   */
  determinePersonalizationStatus$ = createEffect(() => {
    const pgid$ = this.store.pipe(select(getPGID));

    const determined$ = pgid$.pipe(
      map(pgid => !this.apiTokenService.hasUserApiTokenCookie() || pgid),
      whenTruthy(),
      delay(100),
      map(() => personalizationStatusDetermined())
    );

    const changed$ = pgid$.pipe(
      startWith(undefined),
      pairwise(),
      filter(([prev, curr]) => prev !== curr && (!!curr || !!prev)),
      map(() => personalizationStatusChanged())
    );

    return merge(determined$, changed$);
  });

  loadUserCostCenters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserCostCenters),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      filter(([, loggedInCustomer]) => !!loggedInCustomer && loggedInCustomer.isBusinessCustomer),
      mergeMap(() =>
        this.userService.getEligibleCostCenters().pipe(
          map(costCenters => loadUserCostCentersSuccess({ costCenters })),
          mapErrorToAction(loadUserCostCentersFail)
        )
      )
    )
  );

  loadUserPaymentMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserPaymentMethods),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      filter(([, customer]) => !!customer),
      concatMap(([, customer]) =>
        this.paymentService.getUserPaymentMethods(customer).pipe(
          map(result => loadUserPaymentMethodsSuccess({ paymentMethods: result })),
          mapErrorToAction(loadUserPaymentMethodsFail)
        )
      )
    )
  );

  deleteUserPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteUserPaymentInstrument),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      filter(([, customer]) => !!customer),
      concatMap(([payload, customer]) =>
        this.paymentService.deleteUserPaymentInstrument(customer.customerNo, payload.id).pipe(
          mergeMap(() => [
            deleteUserPaymentInstrumentSuccess(),
            loadUserPaymentMethods(),
            displaySuccessMessage(payload.successMessage),
          ]),
          mapErrorToAction(deleteUserPaymentInstrumentFail)
        )
      )
    )
  );

  /**
   * Creates a payment instrument for an un-parametrized payment method (like invoice)  and assigns it as preferred instrument at the user.
   * This is necessary due to limitations of the payment user REST interface.
   */
  updatePreferredUserPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserPreferredPayment),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getLoggedInCustomer))),
      filter(([, customer]) => !!customer),
      concatMap(([payload, customer]) =>
        this.paymentService
          .createUserPayment(customer.customerNo, { id: undefined, paymentMethod: payload.paymentMethodId })
          .pipe(
            mergeMap(pi => [
              updateUser({
                user: { ...payload.user, preferredPaymentInstrumentId: pi.id },
                successMessage: payload.successMessage,
              }),
              loadUserPaymentMethods(),
            ]),
            mapErrorToAction(updateUserFail)
          )
      )
    )
  );

  requestPasswordReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestPasswordReminder),
      mapToPayloadProperty('data'),
      concatMap(data =>
        this.userService
          .requestPasswordReminder(data)
          .pipe(map(requestPasswordReminderSuccess), mapErrorToAction(requestPasswordReminderFail))
      )
    )
  );

  updateUserPasswordByPasswordReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserPasswordByPasswordReminder),
      mapToPayload(),
      concatMap(data =>
        this.userService
          .updateUserPasswordByReminder(data)
          .pipe(
            map(updateUserPasswordByPasswordReminderSuccess),
            mapErrorToAction(updateUserPasswordByPasswordReminderFail)
          )
      )
    )
  );

  updateUserPasswordByPasswordReminderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUserPasswordByPasswordReminderSuccess),
      map(() =>
        displaySuccessMessage({
          message: 'account.profile.update_password.message',
        })
      )
    )
  );
}
